import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Search, 
  Filter, 
  Grid3X3, 
  List, 
  Star, 
  Package, 
  DollarSign, 
  TrendingUp,
  BarChart3,
  PieChart,
  Eye,
  GitCompare,
  Check
} from 'lucide-react';
import { API_URL } from '../../config/api';
import { toast } from '../ui/use-toast';
import { ProductComparisonModal } from './ProductComparisonModal';
import { CatalogAdminTools } from './CatalogAdminTools';
import { useAuth } from '@/context/auth-context';

interface Product {
  id: string;
  url: string;
  title: string;
  name: string;
  brand: string;
  category: string;
  subcategories: string[];
  price: number;
  currency: string;
  priceText: string;
  availability: string;
  rating: number;
  reviewCount: number;
  mainImage: {
    url: string;
    alt: string;
    isProductImage: boolean;
  };
  features: string[];
  specifications: Record<string, string>;
  comparableFields: string[];
}

interface CatalogOverview {
  totalProducts: number;
  categories: Array<{
    name: string;
    count: number;
    brands: string[];
    averagePrice: number;
    priceRange: { min: number; max: number };
  }>;
  priceRange: { min: number; max: number; average: number };
  brands: Array<{
    name: string;
    count: number;
    categories: string[];
    averagePrice: number;
    priceRange: { min: number; max: number };
    averageRating: number;
    totalReviews: number;
  }>;
  availability: {
    inStock: number;
    limited: number;
    outOfStock: number;
    preOrder: number;
    unknown: number;
  };
  products: Product[];
}

interface CatalogViewProps {
  siteId: string;
}

export const CatalogView: React.FC<CatalogViewProps> = ({ siteId }) => {
  const { user } = useAuth();
  const [overview, setOverview] = useState<CatalogOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
  const [selectedAvailability, setSelectedAvailability] = useState<string>('all');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [showComparisonModal, setShowComparisonModal] = useState(false);

  useEffect(() => {
    fetchCatalogOverview();
  }, [siteId]);

  const fetchCatalogOverview = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/catalog/sites/${siteId}/overview`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch catalog data');
      }

      const data = await response.json();
      setOverview(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load catalog');
      toast({
        title: 'Error',
        description: 'Failed to load catalog data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getAvailabilityBadge = (availability: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      in_stock: 'default',
      limited: 'secondary',
      out_of_stock: 'destructive',
      pre_order: 'outline',
      unknown: 'outline',
    };
    return <Badge variant={variants[availability] || 'outline'}>{availability.replace('_', ' ')}</Badge>;
  };

  const getPriceDisplay = (product: Product) => {
    if (product.price) {
      return `${product.currency} ${product.price.toLocaleString()}`;
    }
    return product.priceText || 'Price not available';
  };

  const filteredProducts = (overview?.products || []).filter(product => {
    // Search filter
    if (searchTerm && !product.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !product.brand?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !product.category?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Category filter
    if (selectedCategory !== 'all' && product.category !== selectedCategory) {
      return false;
    }

    // Brand filter
    if (selectedBrand !== 'all' && product.brand !== selectedBrand) {
      return false;
    }

    // Price filter
    if (product.price && (product.price < priceRange[0] || product.price > priceRange[1])) {
      return false;
    }

    // Availability filter
    if (selectedAvailability !== 'all' && product.availability !== selectedAvailability) {
      return false;
    }

    return true;
  });

  const handleProductSelect = (productId: string) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleCompare = () => {
    if (selectedProducts.length < 2) {
      toast({
        title: 'Compare Products',
        description: 'Please select at least 2 products to compare',
        variant: 'destructive',
      });
      return;
    }
    if (selectedProducts.length > 4) {
      toast({
        title: 'Compare Products',
        description: 'You can compare up to 4 products at a time',
        variant: 'destructive',
      });
      return;
    }
    setShowComparisonModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading catalog...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={fetchCatalogOverview}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!overview || overview.totalProducts === 0) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Product Catalog</h1>
          <p className="text-muted-foreground">
            No products found in the catalog
          </p>
        </div>

        {/* Admin Tools - Only show for super admins */}
        {user?.role === 'super_admin' && (
          <CatalogAdminTools 
            siteId={siteId} 
            onRolesAssigned={fetchCatalogOverview}
          />
        )}

        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Products Found</h3>
              <p className="text-muted-foreground mb-4">
                The catalog is empty. This could mean:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 max-w-md mx-auto text-left">
                <li>• Pages haven't been assigned product roles yet</li>
                <li>• The site doesn't have product pages</li>
                <li>• Product pages haven't been crawled or analyzed</li>
              </ul>
              {user?.role === 'super_admin' && (
                <p className="text-sm text-muted-foreground mt-4">
                  Use the admin tools above to auto-assign page roles.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Product Catalog</h1>
          <p className="text-muted-foreground">
            {overview.totalProducts} products across {overview.categories.length} categories
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCompare}
            disabled={selectedProducts.length < 2}
          >
            <GitCompare className="h-4 w-4 mr-2" />
            Compare ({selectedProducts.length})
          </Button>
        </div>
      </div>

      {/* Admin Tools - Only show for super admins */}
      {user?.role === 'super_admin' && (
        <CatalogAdminTools 
          siteId={siteId} 
          onRolesAssigned={fetchCatalogOverview}
        />
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {overview.categories.map(category => (
                    <SelectItem key={category.name} value={category.name}>
                      {category.name} ({category.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Brand */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Brand</label>
              <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                <SelectTrigger>
                  <SelectValue placeholder="All brands" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All brands</SelectItem>
                  {overview.brands.map(brand => (
                    <SelectItem key={brand.name} value={brand.name}>
                      {brand.name} ({brand.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Availability */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Availability</label>
              <Select value={selectedAvailability} onValueChange={setSelectedAvailability}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="in_stock">In Stock ({overview.availability.inStock})</SelectItem>
                  <SelectItem value="limited">Limited ({overview.availability.limited})</SelectItem>
                  <SelectItem value="out_of_stock">Out of Stock ({overview.availability.outOfStock})</SelectItem>
                  <SelectItem value="pre_order">Pre-order ({overview.availability.preOrder})</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Price Range */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Price Range: ${priceRange[0]} - ${priceRange[1]}
            </label>
            <Slider
              value={priceRange}
              onValueChange={setPriceRange}
              max={overview.priceRange.max}
              min={overview.priceRange.min}
              step={10}
              className="w-full"
            />
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">{filteredProducts.length}</p>
                <p className="text-sm text-muted-foreground">Products</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">${overview.priceRange.average.toFixed(0)}</p>
                <p className="text-sm text-muted-foreground">Average Price</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">
                  {(overview.brands.reduce((acc, brand) => acc + brand.averageRating, 0) / overview.brands.length).toFixed(1)}
                </p>
                <p className="text-sm text-muted-foreground">Avg Rating</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-2xl font-bold">{overview.availability.inStock}</p>
                <p className="text-sm text-muted-foreground">In Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Products Grid/List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Products</CardTitle>
              <CardDescription>
                Showing {filteredProducts.length} of {overview.totalProducts} products
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredProducts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No products match your filters</p>
            </div>
          ) : (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
              : 'space-y-4'
            }>
              {filteredProducts.map(product => (
                <Card 
                  key={product.id} 
                  className={`relative cursor-pointer transition-colors hover:shadow-md ${
                    selectedProducts.includes(product.id) ? 'ring-2 ring-primary' : ''
                  }`}
                >
                  {/* Selection checkbox */}
                  <div className="absolute top-2 right-2 z-10">
                    <div
                      className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                        selectedProducts.includes(product.id)
                          ? 'bg-primary border-primary'
                          : 'bg-background border-gray-300 hover:border-primary'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductSelect(product.id);
                      }}
                    >
                      {selectedProducts.includes(product.id) && (
                        <Check className="h-4 w-4 text-white" />
                      )}
                    </div>
                  </div>

                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {product.mainImage?.url && (
                        <img
                          src={product.mainImage.url}
                          alt={product.mainImage.alt || product.name}
                          className="w-16 h-16 object-cover rounded-md"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium truncate pr-8">{product.name}</h3>
                        {product.brand && (
                          <p className="text-sm text-muted-foreground">{product.brand}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="font-semibold">{getPriceDisplay(product)}</span>
                          {product.rating && (
                            <div className="flex items-center gap-1">
                              <Star className="h-3 w-3 fill-current text-yellow-400" />
                              <span className="text-sm">{product.rating}</span>
                              {product.reviewCount && (
                                <span className="text-xs text-muted-foreground">
                                  ({product.reviewCount})
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          {getAvailabilityBadge(product.availability)}
                          {product.category && (
                            <Badge variant="outline" className="text-xs">
                              {product.category}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product Comparison Modal */}
      <ProductComparisonModal
        isOpen={showComparisonModal}
        onClose={() => {
          setShowComparisonModal(false);
          setSelectedProducts([]);
        }}
        siteId={siteId}
        productIds={selectedProducts}
      />
    </div>
  );
}; 